import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { isEmpty, isUndefined, omitBy } from 'lodash'
import { useTranslation } from 'react-i18next'
import Skeleton from 'react-loading-skeleton'
import { useNavigate } from 'react-router-dom'

import * as SvgIcons from 'assets/svg/sprite'
import { IconButton } from 'components/IconButton/IconButton'
import CategoriesSlider from 'components/UI/CategoriesSlider/CategoriesSlider'
import FriendRequestModal from 'components/UI/Modal/FriendRequest/FriendRequestModal'
import { PostPlaceholder } from 'components-ref/molecules'
import { FeedNav } from 'components-ref/organisms/feedNav/FeedNav'
import { useModalContext } from 'context/modalContext'
import { useProfileContext } from 'context/profileContext'
import { useUserContext } from 'context/userContext'
import { usePostsFeed } from 'helpers/hooks'
import { useImpressionsTracker } from 'helpers/hooks/feed/useImpressionsTracker'
import { getSinglePost } from 'services/endpoints/post'
import { POST_FINISHED } from 'services/notifications/notificationTypes'
import { type FeedType, useAccessToken, useFeedTypeStore, useUserProfile } from 'store'
import { useGlobalStore } from 'store/global'
import { useProfileFeedCacheStore } from 'store/profileFeedCacheStore'
import { type IPost } from 'types/interfaces/ITypes'

import VirtualizedPostsFeedList from './VirtualizedPostsFeedList'

import 'react-loading-skeleton/dist/skeleton.css'
import './_postsFeed.scss'

interface DiscoverPostsFeedProps {
  feedNav: FeedType[]
  id: string
  user: any
  username: string
  setSubscribeModalOpen: React.Dispatch<React.SetStateAction<boolean>>
  showFeed?: boolean
  enabled?: boolean
  profileLoading?: boolean
}

const idsSigOf = (arr: Array<{ id: any }> | undefined | null) =>
  arr?.length ? arr.map(p => p.id).join(',') : ''

export const DiscoverPostsFeed = ({
  feedNav,
  id,
  user,
  showFeed = true,
  enabled = true,
  profileLoading = false,
  username,
  setSubscribeModalOpen,
}: DiscoverPostsFeedProps) => {
  const { accountSetupPopup } = useGlobalStore()
  const [categoriesActive, setCategoriesActive] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState<string>('')
  const [initialCategorySlide, setInitialCategorySlide] = useState(0)

  const feed = useFeedTypeStore(state => state.type)
  const modalData = useModalContext()
  const { notificationsChannel } = useUserContext()
  const userData = useUserProfile()
  const navigate = useNavigate()
  const { setProfileMarker } = useProfileContext()
  const isAuthenticated = useAccessToken() !== ''
  const { addDebounceImpression } = useImpressionsTracker()

  const isOwnProfile = userData?.id === id

  const friendsStatus =
    (user?.friends_status ?? user?.friendsStatus ?? user?.friends ?? 'Not friend') as
      | 'Friends'
      | 'Pending'
      | 'Not friend'

  const isFriend = friendsStatus === 'Friends'
  const isPendingFriend = friendsStatus === 'Pending'
  const isNotFriend = friendsStatus === 'Not friend'

  const hasNormalAccess = user?.isSubscribed === true || isOwnProfile || isFriend

  const canQueryFeed =
    enabled && (showFeed || hasNormalAccess)
  const isSubscribed = hasNormalAccess

  const { getFeed, getFeedEntry, setFeed } = useProfileFeedCacheStore()

  const cacheKey = useMemo(() => {
    const profileUsername = user?.username ?? username ?? ''
    const type = feed || 'all'
    const cat = selectedCategory || ''

    return `${profileUsername}:${type}:${cat}`
  }, [user?.username, username, feed, selectedCategory])

  const cachedEntry = getFeedEntry(cacheKey)
  const cachedPosts = getFeed(cacheKey)
  const cachedSig = useMemo(() => idsSigOf(cachedPosts), [cachedPosts])

  const [feedPosts, setFeedPosts] = useState<IPost[]>(cachedPosts ?? [])
  const [hasResolvedInitialLoad, setHasResolvedInitialLoad] = useState(!!cachedEntry)

  const lastAppliedIdsSigRef = useRef<string>(cachedSig)
  const previousCacheKeyRef = useRef(cacheKey)
  const switchedAtRef = useRef(0)
  const lastFeedWriteSigRef = useRef('')

  useLayoutEffect(() => {
    if (previousCacheKeyRef.current !== cacheKey) {
      previousCacheKeyRef.current = cacheKey
      switchedAtRef.current = Date.now()
    }

    const seed = cachedPosts ?? []
    const sig = idsSigOf(seed)

    lastAppliedIdsSigRef.current = sig
    setFeedPosts(seed)
    setHasResolvedInitialLoad(!!cachedEntry)
  }, [cacheKey, cachedSig, cachedPosts, cachedEntry])

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    status,
    dataUpdatedAt,
    isLoading,
  } = usePostsFeed(
      id,
      { type: feed, media_category: selectedCategory },
      cachedPosts,
      isSubscribed,
      cachedEntry?.updatedAt,
      {
          enabled: canQueryFeed,
          refetchOnWindowFocus: false,
      }
  )

  const { t } = useTranslation()

  if (error) {
    console.log(error)
  }

  const ownCategories = Array.isArray(userData?.all_media_categories)
    ? userData?.all_media_categories
    : []

  const viewerCategories = Array.isArray(user?.media_categories)
    ? user.media_categories
    : []

  const categoriesToShow = isOwnProfile
    ? ownCategories
    : user?.isSubscribed === true || isFriend
      ? viewerCategories
      : []

  const showFeedNav = isSubscribed
  const showCategoryControls = isAuthenticated && (isOwnProfile || categoriesToShow.length > 0)

  useEffect(() => {
    if (!showCategoryControls && categoriesActive) {
      setCategoriesActive(false)
    }
  }, [showCategoryControls, categoriesActive])

  useEffect(() => {
    if (!notificationsChannel) return

    const handler = async (d: any) => {
      const postId = d?.post ? String(d.post) : null
      if (!postId) return

      try {
        const res = await getSinglePost(postId)
        const freshPost = (res?.data ?? res) as IPost

        setFeedPosts(prev => {
          const idx = prev.findIndex(p => p.id === postId)
          if (idx === -1) return prev

          const next = prev.slice()
          next[idx] = freshPost

          return next
        })
      } catch (e) {
        console.error('[POST_FINISHED] getSinglePost failed', e)
      }
    }

    notificationsChannel.bind(POST_FINISHED, handler)

    return () => {
      notificationsChannel.unbind(POST_FINISHED, handler)
    }
  }, [notificationsChannel])

  useEffect(() => {
    if (!isSubscribed) {
      setHasResolvedInitialLoad(true)
      return
    }

    const hasRealDataForCurrentTab = !!data && !isLoading

    const totalFlattened =
      hasRealDataForCurrentTab
        ? data.pages?.flatMap((p: any) => p.page.data.data) ?? []
        : cachedPosts ?? []

  const filtered = totalFlattened.filter(
    (p: IPost) =>
      String(
        (p as any).user_id ??
        (p as any).user?.id
      ) === String(id)
  )

    const bump = typeof dataUpdatedAt === 'number' && dataUpdatedAt > 0 ? dataUpdatedAt : 0

    if (switchedAtRef.current > 0 && bump > 0 && bump <= switchedAtRef.current) {
      return
    }

    const idsSig = idsSigOf(filtered)
    const renderSig = `${idsSig}:${bump}`

    if (renderSig !== lastAppliedIdsSigRef.current) {
      lastAppliedIdsSigRef.current = renderSig
      setFeedPosts(filtered)
    }

    if (user?.username) {
      const storeSig = `${cacheKey}:${bump}:${idsSig}`

      if (lastFeedWriteSigRef.current !== storeSig) {
        lastFeedWriteSigRef.current = storeSig

        const pages = (data as any)?.pages
        const lastPage = Array.isArray(pages) && pages.length ? pages[pages.length - 1] : undefined
        const nextCursor = lastPage?.page?.data?.meta?.next_cursor ?? undefined

        setFeed(cacheKey, filtered, {
          nextPageParam: nextCursor ?? null,
          hasNextPage: !!nextCursor,
        })
      }
    }

    if (filtered.length > 0 || !isLoading) {
      setHasResolvedInitialLoad(true)
    }
  }, [
    isSubscribed,
    data,
    dataUpdatedAt,
    cachedPosts,
    id,
    setFeed,
    user?.username,
    cacheKey,
    status,
    isLoading,
  ])

  const addFriend = () => {
    modalData.addModal(
      t('friendRequest'),
      <FriendRequestModal
        id={user.id}
        online={user?.online_status}
        avatar={user?.avatar?.url || user?.cropped_avatar?.url}
        display_name={user?.display_name}
        username={user?.username}
        follower_count={user?.follower_count}
        friend_count={user?.friend_count}
        close={() => modalData.clearModal()}
      />
    )
  }

  const loginAndSubscribe = () => {
    setProfileMarker && setProfileMarker({ profileId: user.id, fromAuth: true })
    navigate('/')
  }

  const dataQuery = useMemo(() => {
      return [
          'getFeed',
          id,
          omitBy(
              { type: feed, media_category: selectedCategory },
              value => value === '' || isUndefined(value) || isEmpty(value)
          ),
      ] as const
  }, [id, feed, selectedCategory])

  const topNav = showFeedNav ? (
    <>
      <div className="profilefeed__nav">
        <FeedNav nav={feedNav} />

        {showCategoryControls && (
          <div className="profilefeed__nav--secondary">
            <IconButton
              icon={<SvgIcons.IconCategories color={categoriesActive ? '#2894FF' : ''} />}
              size="medium"
              type="outline"
              clickFn={() => setCategoriesActive(!categoriesActive)}
            />
          </div>
        )}
      </div>

      {showCategoryControls && (
        <div className="profilefeed_nav profilefeed_nav--secondary">
          <CategoriesSlider
            categoriesOpen={categoriesActive}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            initialCategorySlide={initialCategorySlide}
            setInitialCategorySlide={setInitialCategorySlide}
            categories={categoriesToShow}
            ownProfile={isOwnProfile}
          />
        </div>
      )}
    </>
  ) : null

  if (!isAuthenticated) {
    return (
      <div className={`profilefeed ${accountSetupPopup.visible ? 'extraPad' : ''}`}>
        <PostPlaceholder user={user} btnFn={loginAndSubscribe} btnText={t('subscribe')} />
      </div>
    )
  }

  if (userData?.role === 'model' && !user.isSubscribed && isNotFriend && user.id !== userData?.id) {
    return (
      <div className={`profilefeed ${accountSetupPopup.visible ? 'extraPad' : ''}`}>
        {topNav}
        <PostPlaceholder btnFn={addFriend} user={user} btnText={t('addFriend')} />
      </div>
    )
  }

  if (userData?.role === 'model' && !user.isSubscribed && isPendingFriend && user.id !== userData?.id) {
    return (
      <div className={`profilefeed ${accountSetupPopup.visible ? 'extraPad' : ''}`}>
        {topNav}
        <PostPlaceholder btnFn={() => setSubscribeModalOpen(true)} user={user} btnText={t('subscribe')} />
      </div>
    )
  }

  if ((userData?.role === 'fan' || userData?.role === 'model') && !isSubscribed && user.id !== userData?.id) {
    return (
      <div className={`profilefeed ${accountSetupPopup.visible ? 'extraPad' : ''}`}>
        {topNav}
        <PostPlaceholder btnFn={() => setSubscribeModalOpen(true)} user={user} btnText={t('subscribe')} />
      </div>
    )
  }

  const isInitialLoading =
  (profileLoading || !hasResolvedInitialLoad) &&
  isSubscribed &&
  !feedPosts.length

  if (isInitialLoading) {
    return (
      <div className={`profilefeed ${accountSetupPopup.visible ? 'extraPad' : ''} skeleton`}>
        {topNav}
        <Skeleton borderRadius={20} height={370} />
        <Skeleton style={{ marginTop: 10 }} borderRadius={20} height={50} />
      </div>
    )
  }

  const showEmptyState = hasResolvedInitialLoad && feedPosts.length === 0

  return (
    <div className={`profilefeed ${accountSetupPopup.visible ? 'extraPad' : ''}`}>
      {topNav}

      {feedPosts.length > 0 && (
        <>
          <VirtualizedPostsFeedList
            feedPosts={feedPosts}
            userId={user.id}
            userData={userData}
            addDebounceImpression={addDebounceImpression}
            dataQuery={dataQuery as any}
            fetchNextPage={fetchNextPage}
            hasNextPage={!!hasNextPage}
            isFetchingNextPage={!!isFetchingNextPage}
            estimateRowPx={feed === 'reels' ? 210 : 292}
            rowGapPx={12}
            overscan={2}
            prefetchAhead={feed === 'reels' ? 2 : 1}
            fetchThreshold={4}
            layout={feed === 'reels' ? 'grid' : 'list'}
          />

          <div>{isFetchingNextPage ? <></> : null}</div>
          <div>{isFetching && !isFetchingNextPage ? <></> : null}</div>
        </>
      )}

      {showEmptyState && (
        <div className="profilefeed__noposts">
          {feed === 'videos'
            ? t('userHasNotSharedAnyVideos')
            : feed === 'premium'
              ? t('userHasNotSharedAnyPremiumVideos')
              : t('userHasntPostedAnythingYet')}
        </div>
      )}

      <div className="edit__post__nav--wrapper" />
    </div>
  )
}